/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type ForwardRefExoticComponent,
	type ReactNode,
	type RefAttributes,
	useId,
} from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Heading from '@atlaskit/heading/heading';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { messages } from '../../common/messages';

type ManagedBlockConfigFrameProps = {
	children: ReactNode;
	testId: string;
};

const wrapperStyles = css({
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.200'),
	boxShadow: token('elevation.shadow.overlay'),
});

export const ManagedBlockConfigFrame: ForwardRefExoticComponent<
	ManagedBlockConfigFrameProps & RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, ManagedBlockConfigFrameProps>(({ children, testId }, ref) => {
	const intl = useIntl();
	const headingId = useId();

	return (
		<div
			aria-labelledby={headingId}
			css={wrapperStyles}
			data-testid={testId}
			ref={ref}
			role="group"
			tabIndex={-1}
		>
			<Stack space="space.150">
				<Heading id={headingId} size="small">
					{intl.formatMessage(messages.configureBlock)}
				</Heading>
				{children}
			</Stack>
		</div>
	);
});

ManagedBlockConfigFrame.displayName = 'ManagedBlockConfigFrame';
